import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button/button.component';
import { Icon } from '@shared/components/icon/icon.enum';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import {
  PriorityDragDrop,
  PriorityDragDropItem,
} from '@shared/models/question-types/priority-dragdrop';
import { ValidatorComponent } from '../../validator/validator.component';
import { QuestionBaseComponent } from '../question-base/question-base.component';

type DragSource = 'available' | 'selected';

@Component({
  selector: 'app-priority-dragdrop',
  templateUrl: './priority-dragdrop.component.html',
  styleUrl: './priority-dragdrop.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    QuestionBaseComponent,
    ButtonComponent,
    TranslateModule,
    ValidatorComponent,
  ],
})
export class PriorityDragdropComponent implements OnInit {
  @ViewChild('container') container!: ElementRef;
  @Input({ required: true }) question!: PriorityDragDrop;
  @Input({ required: true }) control!: AbstractControl<
    PriorityDragDropItem[],
    PriorityDragDropItem[]
  >;
  @Input() isFirstQuestion: boolean = false;

  readonly Icon = Icon;
  readonly ValidatorType = ValidatorType;

  availableItems: PriorityDragDropItem[] = [];
  draggedItem: PriorityDragDropItem | null = null;
  draggedOverItem: PriorityDragDropItem | null = null;
  dragSource: DragSource | null = null;
  isDragging: PriorityDragDropItem | null = null;
  maxSelected = false;
  private hasFocus = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.availableItems = this.question.items.filter(
      (item) =>
        !this.control.value.some((selectedItem) => selectedItem.id === item.id),
    );
    this.updateMaxSelectionState();
  }

  private updateMaxSelectionState(): void {
    this.maxSelected =
      Number.isInteger(this.question.maxNumberOfSelections) &&
      this.control.value.length >= this.question.maxNumberOfSelections;
    this.cdr.detectChanges();
  }

  private findItemIndex(
    item: PriorityDragDropItem,
    array: PriorityDragDropItem[],
  ): number {
    return array.findIndex(
      (arrayItem) => arrayItem.id === item.id && arrayItem.title === item.title,
    );
  }

  private handleSameListDrop(
    target: DragSource,
    targetItem?: PriorityDragDropItem,
  ): void {
    if (!this.draggedItem) return;

    if (target === 'selected') {
      const currentValue = [...this.control.value];
      const fromIndex = this.findItemIndex(this.draggedItem, currentValue);
      if (fromIndex === -1) return;

      currentValue.splice(fromIndex, 1);
      const insertIndex = targetItem
        ? this.findItemIndex(targetItem, currentValue)
        : currentValue.length;
      currentValue.splice(insertIndex, 0, this.draggedItem);
      this.control.setValue(currentValue);
    } else {
      const fromIndex = this.findItemIndex(
        this.draggedItem,
        this.availableItems,
      );
      if (fromIndex === -1) return;

      this.availableItems.splice(fromIndex, 1);
      const insertIndex = targetItem
        ? this.findItemIndex(targetItem, this.availableItems)
        : this.availableItems.length;
      this.availableItems.splice(insertIndex, 0, this.draggedItem);
    }
  }

  private handleCrossListDrop(
    target: DragSource,
    targetItem?: PriorityDragDropItem,
  ): void {
    if (!this.draggedItem) return;

    if (this.dragSource === 'available') {
      const fromIndex = this.findItemIndex(
        this.draggedItem,
        this.availableItems,
      );
      if (fromIndex === -1) return;

      this.availableItems.splice(fromIndex, 1);
      const newSelected = [...this.control.value];
      const insertIndex = targetItem
        ? this.findItemIndex(targetItem, newSelected)
        : newSelected.length;
      newSelected.splice(insertIndex, 0, this.draggedItem);
      this.control.setValue(newSelected);
    } else {
      const fromIndex = this.findItemIndex(
        this.draggedItem,
        this.control.value,
      );
      if (fromIndex === -1) return;

      const newSelected = [...this.control.value];
      newSelected.splice(fromIndex, 1);
      this.control.setValue(newSelected);

      const insertIndex = targetItem
        ? this.findItemIndex(targetItem, this.availableItems)
        : this.availableItems.length;
      this.availableItems.splice(insertIndex, 0, this.draggedItem);
    }
  }

  onDragStart(item: PriorityDragDropItem, source: DragSource): void {
    this.draggedItem = item;
    this.dragSource = source;
    this.isDragging = item;
  }

  onDragOver(event: DragEvent, item: PriorityDragDropItem): void {
    event.preventDefault();
    this.draggedOverItem = item;
  }

  onDragLeave(): void {
    this.draggedOverItem = null;
  }

  onDrop(
    event: DragEvent,
    target: DragSource,
    targetItem?: PriorityDragDropItem,
  ): void {
    event.preventDefault();
    if (
      !this.draggedItem ||
      (target === 'selected' &&
        this.maxSelected &&
        this.dragSource === 'available')
    )
      return;

    if (this.dragSource === target) {
      this.handleSameListDrop(target, targetItem);
    } else {
      this.handleCrossListDrop(target, targetItem);
    }

    this.resetDragState();
    this.updateMaxSelectionState();
  }

  private resetDragState(): void {
    this.draggedItem = null;
    this.draggedOverItem = null;
    this.dragSource = null;
    this.isDragging = null;
  }

  moveToSelected(item: PriorityDragDropItem): void {
    if (this.maxSelected) return;

    this.availableItems = this.availableItems.filter((i) => i.id !== item.id);
    this.control.setValue([...this.control.value, item]);
    this.updateMaxSelectionState();
  }

  unselectItem(item: PriorityDragDropItem): void {
    this.control.setValue(this.control.value.filter((i) => i.id !== item.id));
    this.availableItems = [...this.availableItems, item];
    this.updateMaxSelectionState();
  }

  moveItem(item: PriorityDragDropItem, direction: 'up' | 'down'): void {
    const currentValue = [...this.control.value];
    const index = currentValue.findIndex((i) => i.id === item.id);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < currentValue.length) {
      [currentValue[index], currentValue[newIndex]] = [
        currentValue[newIndex],
        currentValue[index],
      ];
      this.control.setValue(currentValue);
    }
  }

  moveUp = (item: PriorityDragDropItem) => this.moveItem(item, 'up');
  moveDown = (item: PriorityDragDropItem) => this.moveItem(item, 'down');

  onDragEnd(): void {
    this.isDragging = null;
  }

  onTouchStart(event: TouchEvent, item: PriorityDragDropItem, source: DragSource): void {
    const target = event.target as HTMLElement;
    // Don't interfere with button clicks
    if (target.closest('app-button') || target.closest('button')) {
      return;
    }

    this.draggedItem = item;
    this.dragSource = source;
    this.isDragging = item;

    // Prevent default to avoid scrolling while dragging
    event.preventDefault();
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.draggedItem) return;

    event.preventDefault();
    const touch = event.touches[0];
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

    if (elementBelow) {
      const listItem = elementBelow.closest('li[role="option"]');
      if (listItem) {
        const itemId = listItem.getAttribute('data-item-id');
        if (itemId) {
          const availableItem = this.availableItems.find(i => i.id === itemId);
          const selectedItem = this.control.value.find(i => i.id === itemId);
          this.draggedOverItem = availableItem || selectedItem || null;
        }
      }
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (!this.draggedItem) return;

    event.preventDefault();
    const touch = event.changedTouches[0];
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

    if (elementBelow) {
      const availableContainer = elementBelow.closest('[data-container="available"]');
      const selectedContainer = elementBelow.closest('[data-container="selected"]');

      let target: DragSource | null = null;
      let targetItem: PriorityDragDropItem | undefined = undefined;

      if (availableContainer) {
        target = 'available';
        const listItem = elementBelow.closest('li[role="option"]');
        if (listItem) {
          const itemId = listItem.getAttribute('data-item-id');
          targetItem = this.availableItems.find(i => i.id === itemId);
        }
      } else if (selectedContainer) {
        target = 'selected';
        const listItem = elementBelow.closest('li[role="option"]');
        if (listItem) {
          const itemId = listItem.getAttribute('data-item-id');
          targetItem = this.control.value.find(i => i.id === itemId);
        }
      }

      // Only proceed with drop if we have a valid target
      if (target) {
        if (
          target === 'selected' &&
          this.maxSelected &&
          this.dragSource === 'available'
        ) {
          this.resetDragState();
          return;
        }

        if (this.dragSource === target) {
          this.handleSameListDrop(target, targetItem);
        } else {
          this.handleCrossListDrop(target, targetItem);
        }

        this.updateMaxSelectionState();
      }
    }

    this.resetDragState();
  }

  onFocusIn(): void {
    this.hasFocus = true;
  }

  onFocusOut(): void {
    requestAnimationFrame(() => {
      if (!this.container.nativeElement.contains(document.activeElement)) {
        this.hasFocus = false;
        this.control.markAsTouched();
      }
    });
  }
}
